// @ts-strict-ignore
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Button,
  Modal,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  Skeleton,
  Typography,
  useToast,
  Tooltip,
  Link,
} from "@aviary";
import { l } from "@clinic/emerson/locales/i18n";
import { useMapAgreementCreateMutation } from "@clinic/shared/data/mutations/UsersMapAgreementCreateMutation/UsersMapAgreementCreate.mutation";
import { useMapAgreementByIdQuery } from "@clinic/shared/data/queries/MapAgreementByIdQuery/MapAgreementById.query";
import { usePractitionerQuery } from "@clinic/shared/data/queries/PractitionerQuery/Practitioner.query";
import { useWholesalePolicyLink } from "@emerson/hooks/useWholesalePolicyLink/useWholesalePolicyLink";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { BrandAgreementEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import {
  useAnalyticsLogger,
  MapAgreementType,
} from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";

import { BasicMapAgreement } from "./BasicMapAgreement/BasicMapAgreement";
import { LazyStrictMapAgreement } from "./StrictMapAgreement/LazyStrictMapAgreement";

import * as styles from "./MapAgreementModal.styles";

interface Props {
  isOpen: boolean;
  mapAgreementId: string;
  onResolved: () => void;
  onRejected: () => void;
  entryPoint: BrandAgreementEntryPoint;
  automaticEvent?: boolean;
}

const MapAgreementModal = ({
  isOpen,
  onResolved,
  onRejected,
  mapAgreementId,
  entryPoint,
  automaticEvent = true,
}: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const wholesalePolicyLink = useWholesalePolicyLink();
  const logAnalyticsEvent = useAnalyticsLogger();

  const { data: userPractitionerData, loading: practitionerQueryLoading } = usePractitionerQuery();
  const { loading: loadingAgreement, data: mapAgreementData } = useMapAgreementByIdQuery({
    variables: { mapId: mapAgreementId },
    skip: !isOpen,
  });

  const [agreeDisagreeToMap, { loading: mutationLoading }] = useMapAgreementCreateMutation({
    onCompleted: data => {
      const errorMessage = data?.usersMapAgreementCreate?.errors?.message;

      const status = data?.usersMapAgreementCreate?.usersMapAgreement?.status;

      if (errorMessage) {
        makeToast("error", errorMessage);
      } else if (status === "ACCEPTED") {
        onResolved();
      } else {
        onRejected();
      }
    },
    onError: error => makeToast("error", error.message),
  });

  const [isAgreeButtonEnabled, setIsAgreeButtonEnabled] = useState<boolean>(false);

  const isStoreOwner = userPractitionerData?.currentPractitioner?.isStoreOwner;
  const isStrictAgreement = mapAgreementData?.viewer?.mapAgreements?.agreementType === "STRICT";
  const brandMapAgreements = mapAgreementData?.viewer?.mapAgreements?.brandsMapAgreements;
  const agreementBrand = brandMapAgreements?.[0]?.brand?.name;
  const agreementType = isStrictAgreement ? MapAgreementType.SINGLE : MapAgreementType.MULTI;

  const logViewBrandAgreementEvent = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_WHOLESALE_POLICIES_VIEW_BRAND_AGREEMENT,
      payload: {
        entry_point: entryPoint,
        brand: agreementBrand,
        agreement_type: agreementType,
        automatic_event: automaticEvent,
      },
    });
  };

  useEffect(() => {
    if (
      isOpen &&
      !loadingAgreement &&
      !practitionerQueryLoading &&
      isStoreOwner &&
      brandMapAgreements?.length > 0
    ) {
      logViewBrandAgreementEvent();
    }
  }, [isOpen, loadingAgreement, practitionerQueryLoading]);

  const handleAccept = () => {
    agreeDisagreeToMap({
      variables: {
        input: {
          mapAgreementId,
          status: "ACCEPTED",
        },
      },
    });
    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_WHOLESALE_POLICIES_ACCEPT_BRAND_AGREEMENT,
      payload: {
        entry_point: entryPoint,
        agreement_type: agreementType,
        brand: mapAgreementData?.viewer?.mapAgreements?.brandsMapAgreements[0]?.brand?.name,
        automatic_event: false,
      },
    });
  };

  const handleReject = () => {
    if (isStoreOwner) {
      agreeDisagreeToMap({
        variables: {
          input: {
            mapAgreementId,
            status: "REJECTED",
          },
        },
      });
    }
    onRejected();
  };

  const renderAgreement = () => {
    if (loadingAgreement || practitionerQueryLoading) {
      return (
        <div css={styles.skeletonContainer}>
          <Skeleton type="button" isFullWidth />
          <Skeleton type="button" isFullWidth css={styles.skeletonBottom} />
        </div>
      );
    }
    if (isStrictAgreement) {
      return (
        <LazyStrictMapAgreement
          mapAgreementUrl={mapAgreementData?.viewer?.mapAgreements?.agreementUrl}
          brandName={mapAgreementData?.viewer?.mapAgreements?.brandsMapAgreements[0]?.brand?.name}
          onReadToBottom={() => setIsAgreeButtonEnabled(true)}
        />
      );
    } else {
      return (
        <BasicMapAgreement
          onCheckboxChange={() => setIsAgreeButtonEnabled(!isAgreeButtonEnabled)}
          isAccepted={isAgreeButtonEnabled}
        />
      );
    }
  };

  const renderAgreeButton = () => {
    if (!isAgreeButtonEnabled && isStrictAgreement) {
      return (
        <Tooltip tooltipContent={t(l.common.YouMustScroll)} data-testid="scroll-tooltip">
          <Button
            isFullWidth
            css={styles.agreeButton}
            disabled={!isAgreeButtonEnabled}
            onClick={handleAccept}
            isLoading={mutationLoading}
          >
            {t(l.common.IAgree)}
          </Button>
        </Tooltip>
      );
    }

    return (
      <Button
        isFullWidth
        css={styles.agreeButton}
        disabled={!isAgreeButtonEnabled}
        onClick={handleAccept}
        isLoading={mutationLoading}
        data-e2e="agree-with-policy-button"
      >
        {t(l.common.IAgree)}
      </Button>
    );
  };

  const renderModalContent = () => {
    if (!isStoreOwner) {
      return (
        <>
          <Typography css={styles.nonOwnerText}>
            {t(l.common.OnlyDispensaryOwnersCanDoIt)}
          </Typography>
          <ModalFooter css={styles.bottomActions}>
            <Button isFullWidth css={styles.okButton} onClick={handleReject}>
              {t(l.common.Ok)}
            </Button>
          </ModalFooter>
        </>
      );
    }

    return (
      <>
        {renderAgreement()}
        <ModalFooter css={styles.bottomActions}>
          <Typography css={styles.footerText}>
            <Trans i18nKey={l.common.MAPModalFooterText}>
              MAP policies for all applicable brands can be found and referenced at our
              <Link
                target="_blank"
                rel="noopener noreferrer"
                to={wholesalePolicyLink}
                aria-label={t(l.wholesalePolicies.GoToWholeSalePolicies)}
              >
                {t(l.common.PoliciesPage)}
              </Link>
              .
            </Trans>
          </Typography>
          {renderAgreeButton()}
        </ModalFooter>
      </>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleReject}
      size={!isStoreOwner ? "small" : "large"}
      hideClose={!isStoreOwner}
    >
      <ModalContainer css={!!isStoreOwner && !!isStrictAgreement && styles.modalContainerForPdf}>
        <ModalHeader>
          <Typography type="h5" css={!isStoreOwner && styles.centerTitle}>
            {t(l.common.SupplierMapAgreement)}
          </Typography>
        </ModalHeader>
        {renderModalContent()}
      </ModalContainer>
    </Modal>
  );
};
export { MapAgreementModal };
